import app from 'flarum/common/app';
export { default as ArenaCard } from './models/ArenaCard';
export { default as ArenaBattle } from './models/ArenaBattle';

// ── Elemental roster ─────────────────────────────────────────────────────────
// Elements are admin-configurable (name, icon, color, advantages) and no
// longer a fixed five — everything about them lives in ./elements. The
// ELEMENT_LABELS/ICONS/COLORS maps re-exported here are live views over that
// roster, so the call sites that read them by key work unchanged.

export { ELEMENT_LABELS, ELEMENT_ICONS, ELEMENT_COLORS, ARENA_ELEMENTS, BUILTIN_ELEMENTS, DEFAULT_ELEMENT_COLOR, DEFAULT_ELEMENT_ICON, setArenaElements, resolveElements, parseElementList, findElement, elementKeys, elementLabel, elementIcon, elementColor, elementBeats, elementAdvantagePairs, isBuiltinElement, sanitizeElementKey, elementKeyFromName } from './elements';
// ── Bonus card constants ─────────────────────────────────────────────────────

export const BONUS_EFFECT_LABELS = {
  heal: 'Исцеление',
  shield: 'Щит',
  regeneration: 'Регенерация',
  poison: 'Яд',
  weaken: 'Ослабление',
  reflect: 'Отражение',
  mana_steal: 'Похищение',
  power_surge: 'Всплеск',
  debuff_remove: 'Очищение',
  overload: 'Выстрел',
  resonance: 'Резонанс',
  sacrifice: 'Жертва',
  swap_stats: 'Перестановка',
  random_effect: 'Рандом',
  amplify: 'Усиление',
  copy: 'Копирование',
  elemental_tide: 'Прилив',
  elemental_dominance: 'Превосходство',
  preparation: 'Подготовка',
  involution: 'Инволюция',
  antimagic: 'Антимагия',
  oath: 'Клятва'
};
export const BONUS_EFFECT_ICONS = {
  heal: 'fas fa-heart',
  shield: 'fas fa-shield-alt',
  regeneration: 'fas fa-tint',
  poison: 'fas fa-skull-crossbones',
  weaken: 'fas fa-arrow-down',
  reflect: 'fas fa-undo-alt',
  mana_steal: 'fas fa-hand-holding-water',
  power_surge: 'fas fa-plus-circle',
  debuff_remove: 'fas fa-broom',
  overload: 'fas fa-crosshairs',
  resonance: 'fas fa-bolt',
  sacrifice: 'fas fa-heart-broken',
  swap_stats: 'fas fa-exchange-alt',
  random_effect: 'fas fa-dice',
  amplify: 'fas fa-angle-double-up',
  copy: 'fas fa-copy',
  elemental_tide: 'fas fa-wave-square',
  elemental_dominance: 'fas fa-crown',
  preparation: 'fas fa-piggy-bank',
  involution: 'fas fa-compress-arrows-alt',
  antimagic: 'fas fa-ban',
  oath: 'fas fa-scroll'
};
export const BONUS_EFFECT_COLORS = {
  heal: '#80b77e',
  shield: '#19647E',
  regeneration: '#06b6d4',
  poison: '#60992D',
  weaken: '#fda371',
  reflect: '#FCB1A6',
  mana_steal: '#9b5de5',
  power_surge: '#FFA51F',
  debuff_remove: '#e2e8f0',
  overload: '#CC005F',
  resonance: '#FFD400',
  sacrifice: '#ec4899',
  swap_stats: '#14b8a6',
  random_effect: '#893168',
  amplify: '#f43f5e',
  copy: '#4C6EF5',
  elemental_tide: '#0EA5E9',
  elemental_dominance: '#7C3AED',
  preparation: '#10B981',
  involution: '#64748B',
  antimagic: '#797A9E',
  oath: '#877666'
};

// app.translator.trans() returns Mithril.Children, not a guaranteed plain
// string: with parameters it comes back as an array of string parts, which
// coerces to a comma-joined mess ("Steals ,2, mana") if handed directly to
// something expecting a real string (HTML attribute, confirm()/alert()).
// Joining with '' instead yields the correct plain string.
export function transPlain(id, parameters) {
  const translated = app.translator.trans(id, parameters);
  return Array.isArray(translated) ? translated.join('') : String(translated);
}

// Effect descriptions live in locale (forumaker-arena.lib.bonus_effect_descriptions.*),
// already parametrized with ICU plural rules — see transPlain() above for why
// this can't just be app.translator.trans() directly.
export function formatBonusDescription(effect, value) {
  return transPlain(`forumaker-arena.lib.bonus_effect_descriptions.${effect}`, {
    value: value ?? '?'
  });
}
export const BONUS_COLOR = '#6366f1'; // default color for bonus cards with no effect set

// ── Mutable settings (overridden at runtime from admin) ──────────────────────
export const ARENA_SETTINGS = {
  manaLabel: 'Мана'
};

/**
 * Icons for the four core stats, admin-overridable from the "Кнопки и иконки"
 * settings block.
 *
 * These are the glyphs that stand for HP, mana, attack and defense wherever a
 * number is shown — on cards, in battle posts, in the challenge dialog, on
 * Battle Pass rewards. They are NOT the bonus-effect icons: the "Исцеление"
 * effect happens to default to a heart too, but it's configured separately
 * through BONUS_EFFECT_ICONS so an admin can restyle one without the other.
 *
 * Mutated in place at boot (see the forum/admin initializers) rather than
 * reassigned, so every module that imported this object sees the change.
 */
export const ARENA_ICONS = {
  hp: 'fas fa-heart',
  mana: 'fas fa-gem',
  attack: 'fas fa-hand-fist',
  defense: 'fas fa-shield-alt'
};

// Card backs: a dynamic, admin-configurable list (name, image, win
// threshold). id 0 ("no card back") isn't stored here — it's always
// available by default. Populated on the forum via mutation (length=0 +
// push) rather than reassignment, so every module that imported this
// constant sees the updated data through the same reference.

export const ARENA_CARD_BACKS = [];

/** Card back image by id (0/null → no card back → null). */
export function getCardBackImage(id) {
  if (!id) return null;
  return ARENA_CARD_BACKS.find(b => b.id === id)?.imageUrl || null;
}

/** Card back name by id (0/null → no card back → null). */
export function getCardBackName(id) {
  if (!id) return null;
  return ARENA_CARD_BACKS.find(b => b.id === id)?.name || null;
}

// ── Contrast helper ──────────────────────────────────────────────────────────
// Returns '#fff' or 'rgba(0,0,0,0.82)' based on background brightness.
export function getContrastColor(hex) {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 135 ? 'rgba(0,0,0,0.82)' : '#fff';
}